/**
 * @file PaymentsView.tsx
 *
 * The Payments & VAT overview screen, mirroring WSB-v2's Payment tab landing
 * page. Loads the payment gateways and VAT settings, derives each option's
 * Active/Inactive status badge, and renders three option cards (Shore Pay,
 * Manual payment, VAT & taxes) whose CTAs route to the matching sub-pages.
 * Reached via the `/payments` nav tab; as a top-level tab it has no back
 * breadcrumb (the sub-pages link back to it). Mock-backed today.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TileName } from '@group.one/gravity';
import {
  Badge,
  Button,
  ContentContainer,
  Icon,
  Loader,
  PayIcon,
  Tile,
} from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { fetchPaymentGateways, fetchVatSettings } from '../api/payments';
import type { PaymentGateway, VatSettings } from '../api/payments-types';
import {
  MANUAL_GATEWAY_ID,
  PAYMENT_METHOD_ICONS,
  SHORE_GATEWAY_ID,
} from './payments-constants';
import styles from './payments.module.css';

/** A single payment option's call-to-action. */
interface CardCta {
  text: string;
  withIcon?: boolean;
  testId: string;
  onClick: () => void;
}

interface PaymentCardProps {
  testId: string;
  iconName: string;
  title: string;
  subtitle: string;
  active: boolean;
  statusText: string;
  cta: CardCta;
  children?: ReactNode;
}

/** Renders the CTA button; placed both in-content (mobile) and at the card's right (desktop). */
function CtaButton({ cta, className }: { cta: CardCta; className: string }) {
  return (
    <Button
      variant="secondary"
      condensed
      className={className}
      data-testid={cta.testId}
      onClick={cta.onClick}
    >
      {cta.withIcon ? <Icon name="add" /> : null}
      {cta.text}
    </Button>
  );
}

/** One payment-option card: tile + title/badge + subtitle + CTA + optional details. */
function PaymentCard({
  testId,
  iconName,
  title,
  subtitle,
  active,
  statusText,
  cta,
  children,
}: PaymentCardProps) {
  return (
    <ContentContainer className="gv-mt-lg">
      <div
        className="gv-flex gv-p-lg gv-flex-col gv-tab-flex-row gv-gap-md"
        data-testid={testId}
      >
        <Tile name={iconName as TileName} type="duotone" size="sm" />
        <div className="gv-flex gv-w-full gv-flex-col gv-gap-sm">
          <div className="gv-flex gv-w-full gv-justify-between">
            <div className="gv-flex gv-gap-sm gv-items-center">
              <span className="gv-text-md gv-text-bold">{title}</span>
              <Badge variant={active ? 'info' : 'generic'}>{statusText}</Badge>
            </div>
          </div>
          <div className="gv-w-full">
            <div className="gv-text-sm">{subtitle}</div>
            <CtaButton
              cta={{ ...cta, testId: `${cta.testId}-mobile` }}
              className="gv-tab-hidden gv-max-mob-w-full gv-mt-md"
            />
            {children}
          </div>
        </div>
        <CtaButton cta={cta} className="gv-max-mob-hidden gv-flex-shrink-0" />
      </div>
    </ContentContainer>
  );
}

/** The checkmark benefits list and supported-method logos for the Shore Pay card. */
function ShoreBenefits() {
  const intl = useIntl();
  const benefits = [
    adminMessages.paymentsShoreBenefitStripe,
    adminMessages.paymentsShoreBenefitFees,
    adminMessages.paymentsShoreBenefitSupport,
    adminMessages.paymentsShoreBenefitPayouts,
  ];
  return (
    <div className="gv-flex gv-flex-col gv-gap-sm gv-mt-md">
      <ul className="gv-flex gv-flex-col gv-gap-md">
        {benefits.map((benefit) => (
          <li className="gv-flex gv-gap-sm" key={benefit.id}>
            <Icon name="check" />
            <span className="gv-text-sm">{intl.formatMessage(benefit)}</span>
          </li>
        ))}
      </ul>
      <div className="gv-flex gv-flex-wrap gv-gap-sm gv-items-center gv-pl-lg">
        {PAYMENT_METHOD_ICONS.map((name) => (
          <PayIcon key={name} name={name} />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders the Payments & VAT overview.
 *
 * @returns The payments overview screen.
 */
export function PaymentsView() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [vat, setVat] = useState<VatSettings>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void Promise.all([
      fetchPaymentGateways().catch(() => [] as PaymentGateway[]),
      fetchVatSettings().catch(() => undefined),
    ]).then(([loadedGateways, loadedVat]) => {
      if (!active) return;
      setGateways(loadedGateways);
      setVat(loadedVat);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="gv-flex gv-justify-center gv-p-lg">
        <Loader condensed />
      </div>
    );
  }

  // Shore is CONNECTED once a method exists (the gateway is present) — even while its account is
  // still pending KYC. Branching on existence (not just `enabled`) is what stops the overview from
  // offering "Connect" a second time and minting a duplicate account.
  const shoreGateway = gateways.find((g) => g.id === SHORE_GATEWAY_ID);
  const shoreConnected = Boolean(shoreGateway);
  const shoreActive = Boolean(shoreGateway?.enabled);
  const manualActive = Boolean(gateways.find((g) => g.id === MANUAL_GATEWAY_ID)?.enabled);
  const vatActive = Boolean(vat?.enabled);

  const statusText = (isActive: boolean): string =>
    intl.formatMessage(
      isActive ? adminMessages.paymentsStatusActive : adminMessages.paymentsStatusInactive,
    );

  // Shore card copy by state: no account → "Connect" (+); pending → "Continue setup"; active → "Manage".
  const shoreStatusText = shoreActive
    ? statusText(true)
    : shoreConnected
      ? intl.formatMessage(adminMessages.paymentsStatusPending)
      : statusText(false);
  const shoreCtaText = shoreActive
    ? intl.formatMessage(adminMessages.paymentsActionManage)
    : shoreConnected
      ? intl.formatMessage(adminMessages.paymentsActionContinueSetup)
      : intl.formatMessage(adminMessages.paymentsActionConnect);

  return (
    <div className={styles.form}>
      <div className={`gv-w-full ${styles.column}`}>
        <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
          <h2 className="gv-heading-lg gv-m-0">
            <FormattedMessage {...adminMessages.paymentsTitle} />
          </h2>
          <p className="gv-text-sm gv-m-0">
            <FormattedMessage {...adminMessages.paymentsDescription} />
          </p>
        </header>

        <PaymentCard
          testId="payments-card-shore"
          iconName="shore"
          title={intl.formatMessage(adminMessages.paymentsShoreTitle)}
          subtitle={intl.formatMessage(adminMessages.paymentsShoreSubtitle)}
          active={shoreActive}
          statusText={shoreStatusText}
          cta={{
            text: shoreCtaText,
            // The "+" (add) icon only for a genuine first-time connect; once an account exists the
            // CTA resumes/manages it, never creates another.
            withIcon: !shoreConnected,
            testId: 'payments-shore-cta',
            onClick: () => navigate('/payments/shore-pay/connect'),
          }}
        >
          <ShoreBenefits />
        </PaymentCard>

        <PaymentCard
          testId="payments-card-manual"
          iconName="paypal-was refunded v2"
          title={intl.formatMessage(adminMessages.paymentsManualCardTitle)}
          subtitle={intl.formatMessage(adminMessages.paymentsManualCardSubtitle)}
          active={manualActive}
          statusText={statusText(manualActive)}
          cta={{
            text: intl.formatMessage(
              manualActive
                ? adminMessages.paymentsActionManage
                : adminMessages.paymentsActionAddOption,
            ),
            withIcon: !manualActive,
            testId: 'payments-manual-cta',
            onClick: () => navigate('/payments/manual'),
          }}
        />

        <PaymentCard
          testId="payments-card-vat"
          iconName="price-tag"
          title={intl.formatMessage(adminMessages.paymentsVatCardTitle)}
          subtitle={intl.formatMessage(adminMessages.paymentsVatCardSubtitle)}
          active={vatActive}
          statusText={statusText(vatActive)}
          cta={{
            text: intl.formatMessage(adminMessages.paymentsActionManage),
            testId: 'payments-vat-cta',
            onClick: () => navigate('/payments/vat'),
          }}
        />
      </div>
    </div>
  );
}
